/**
 * Presentation helpers for the notification bell (Phase 7).
 *
 * Pure and framework-free so they can be unit-tested without a browser: the
 * component only maps the output to markup.
 */

import type { Notification, NotificationSeverity } from '../api/types';

/** Mirrors `apps/api/src/notifications/domain.ts` — keep both in step. */
const DAY_MS = 86_400_000;

export interface NotificationCopy {
  /** One-line headline, e.g. "Expires in 7 days". */
  title: string;
  /** Optional second line (renewal cost, block reason, ...). */
  detail: string | null;
  /** Row accent: red for expired/overdue, amber for warnings, blue for events. */
  severity: NotificationSeverity;
  /** Kind icon key. */
  icon: 'clock' | 'refresh' | 'status';
}

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const asNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const asString = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;

/** `-2 d` / `in 3 h` / `5 d ago` — the list's secondary label. */
export function relativeTime(iso: string | null, now: number = Date.now()): string | null {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;

  const diffMs = target - now;
  const minutes = Math.floor(Math.abs(diffMs) / 60_000);
  let label: string;
  if (minutes < 1) return 'just now';
  else if (minutes < 60) label = `${minutes} min`;
  else if (minutes < 48 * 60) label = `${Math.round(minutes / 60)} h`;
  else label = `${Math.round(minutes / (60 * 24))} d`;

  return diffMs < 0 ? `${label} ago` : `in ${label}`;
}

/** Whole days until `iso` (negative when past), `null` when unparseable. */
export function daysLeft(iso: string | null, now: number = Date.now()): number | null {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  return Math.ceil((target - now) / DAY_MS);
}

/** Human wording for one notification, derived from its payload. */
export function describeNotification(notification: Notification, now: number = Date.now()): NotificationCopy {
  const payload = asRecord(notification.payload);
  const severity = asString(payload.severity);
  const name = notification.subscription?.name ?? asString(payload.subscriptionName) ?? 'Subscription';

  if (notification.kind === 'EXPIRY') {
    const left = asNumber(payload.daysLeft) ?? daysLeft(asString(payload.expiresAt), now);
    if (payload.overdue === true || (left !== null && left < 0)) {
      return {
        title: `${name} expired`,
        detail: left !== null ? `${Math.abs(left)} d past due` : null,
        severity: 'red',
        icon: 'clock',
      };
    }
    return {
      title: `${name} expires soon`,
      detail: left !== null ? `in ${left} day${left === 1 ? '' : 's'}` : null,
      severity: 'amber',
      icon: 'clock',
    };
  }

  if (notification.kind === 'RENEWAL') {
    const left = asNumber(payload.daysLeft);
    const cost = asNumber(payload.renewalCost);
    const currency = asString(payload.currency) ?? '';
    const overdue = payload.overdue === true || (left !== null && left < 0);
    const amount = cost !== null ? `${cost} ${currency}` : null;

    return {
      title: `${name} renewal ${overdue ? 'overdue' : 'due'}`,
      detail:
        left === null
          ? amount
          : `${overdue ? `${Math.abs(left)} d overdue` : `in ${left} d`}${amount ? ` · ${amount}` : ''}`,
      severity: overdue ? 'red' : 'amber',
      icon: 'refresh',
    };
  }

  const to = asString(payload.to) ?? 'changed';
  const from = asString(payload.from);
  const reason = asString(payload.reason);

  return {
    title: `${name} ${to.toLowerCase() === 'active' ? 'is active again' : `is now ${to.toLowerCase()}`}`,
    detail: reason ?? (from ? `was ${from.toLowerCase()}` : null),
    severity: severity === 'red' ? 'red' : 'blue',
    icon: 'status',
  };
}

/** Sorts unread first, then newest — the order the popover shows. */
export function byUnreadThenNewest(a: Notification, b: Notification): number {
  if (Boolean(a.readAt) !== Boolean(b.readAt)) return a.readAt ? 1 : -1;
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}
